import { Injectable } from '@angular/core';
import { ColDef, ValueFormatterParams } from 'ag-grid-enterprise';
import { Formatter } from '@aptitude/shared-vanilla';

import { GridTemplateService } from '../grid-template.service';

export enum GenericColumnTypes {
  CURRENCY_COLUMN = 'currencyColumn',
  EDITABLE_NUMERIC_COLUMN = 'editableNumericColumn',
  DEFAULT_COLUMN = 'defaultColumn',
}

/**
 * These are the standard column sizes. They should be applied to both minWidth and maxWidth to keep from resizing.
 * Grids should have all but one column set to these width, the flex column should fill out the space needed.
 *
 * Examples of usage per size are:
 * EXTRA_SMALL - icons, alerts, badges
 * SMALL - city/state, conversions, impact %, toggle buttons (on/off), target
 * MEDIUM - spend, avg last price paid, savings potential
 * LARGE - descriptions
 */
export enum ColumnSize {
  EXTRA_SMALL = 80,
  SMALL = 120,
  MEDIUM = 160,
  LARGE = 200,
  EXTRA_LARGE = 240,
}

@Injectable({
  providedIn: 'root',
})
export class GridGenericColumnDefinitionsService {
  constructor(private gridTemplateService: GridTemplateService) {}

  /**
   * @description These are meant to be generic so they should not contain any
   * domain are data specific properties.
   */
  public get columnDefinitions() {
    return {
      /**
       * @description It is best to use directly as the value then as a type because if type is assigned in defaultColDef
       * and the columnDef also has a type assigned, then it will not automatically inherit defaults.
       */
      [GenericColumnTypes.DEFAULT_COLUMN]: {
        minWidth: 100,
        sortingOrder: ['asc', 'desc'],
        sortable: true,
        resizable: true,
        filter: true,
        flex: 1,
        headerComponentParams: {
          template:
            this.gridTemplateService.agGridColumnHeaderTemplateForTwoLineText(),
        },
      },

      [GenericColumnTypes.CURRENCY_COLUMN]: {
        filter: 'agNumberColumnFilter',
        valueFormatter: (params: ValueFormatterParams) => {
          return Formatter.currency(params.value);
        },
      },

      [GenericColumnTypes.EDITABLE_NUMERIC_COLUMN]: {
        cellEditor: 'agTextCellEditor',
        filter: 'agNumberColumnFilter',
        editable: true,
        enableCellChangeFlash: true,
        cellRenderer: 'agAnimateShowChangeCellRenderer',
        // Return new value if valid number else old value.
        valueParser: ({ newValue, oldValue }) => {
          const parsedValue = parseFloat(newValue);
          return isFinite(parsedValue) ? parsedValue : oldValue;
        },
      },
    } as Record<GenericColumnTypes, ColDef>;
  }
}
